import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useUserState, { actions } from './useUserState';
import { useUserContext } from '../UserContext';

export const useLoginUser = ({ organization: organizationId, invitation }) => {
  const { setState, setToken } = useUserContext();
  const { isAuthenticated, getIdTokenClaims, loginWithRedirect, isLoading, error } = useAuth0();
  const [state, dispatch] = useUserState();

  const isInvitation = !!(organizationId && invitation);
  // If it's an invitation, we should immediately go through the login flow, regardless
  // of whether the user is already authenticated.
  const shouldLogin = (!isLoading && !error && !isAuthenticated) || isInvitation;

  useEffect(() => {
    if (isAuthenticated && !state.isTokenValid) {
      getIdTokenClaims().then(idToken => {
        // Set the initial token in the ref (for Apollo auth)
        setToken(idToken.__raw);

        dispatch({
          type: actions.SET_USER_INFO,
          payload: {
            tokenInfo: idToken,
          },
        });
      });
    }

    if (shouldLogin) {
      dispatch({
        type: actions.RESET_USER_INFO,
      });
      localStorage.setItem('redirectPath', window.location.pathname);
      const options = {
        // appState: { target: window.location.href}
      };

      if (isInvitation) {
        // auth0-react reads `organization`, not `organizationId`; the SDK only
        // renames it internally after the fact. Auth0 rejects an `invitation`
        // that arrives without an organization, so the wrong key here breaks
        // the invite flow for anyone without an org hint cookie already set.
        options.organization = organizationId;
        options.invitation = invitation;
      }

      // TODO (Ohms 2022.12.01) - Investigate why using handleRedirectCallback is not working
      //    https://community.auth0.com/t/how-do-i-set-up-a-dynamic-allowed-callback-url/60268
      //    prefer that over using the localStorage
      loginWithRedirect(options).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getIdTokenClaims,
    isAuthenticated,
    state.isTokenValid,
    dispatch,
    isLoading,
    loginWithRedirect,
    error,
    isInvitation,
    shouldLogin,
    organizationId,
    invitation,
  ]);

  useEffect(() => {
    if (error) {
      dispatch({
        type: actions.SET_ERROR,
        payload: {
          error: error,
        },
      });
    }
  }, [error, dispatch]);

  useEffect(() => {
    setState(state);
  }, [state, setState]);
};
